/* eslint-disable @next/next/no-img-element */
'use client'

import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import dayjs from 'dayjs'

import { yupResolver } from '@hookform/resolvers/yup'

import { formatConsentToAPIFormat } from '~/utils/formatConsentToAPIFormat'

import { useNewsletterSignUpMutation, UserConsentTypeEnum } from '~/app/generated/graphql'
import { Maybe } from '~/app/generated/strapi'
import { gt } from '~/lib/gt'

import { Button } from '../Button/Button'
import Checkbox from '../Checkbox'
import Collapse from '../Collapse/CollapseView'
import { DangerousTranslate } from '../DangerousTranslate'
import { FormInput } from '../FormInput/FormInput'
import Icon from '../Icon/IconView'
import Link from '../Link'
import { useToast } from '../Toast'

import styles from './Footer.module.css'
import { FooterSection } from './FooterItems'
import { defaultToast, joinTheClubSchema, PartnerGroup, Social } from './FooterUtils'

type Props = {
  items?: FooterSection[]
  theme?: typeof styles
  logoImg?: Maybe<string>
  partners?: Array<PartnerGroup>
  socials?: Array<Social>
  copyright?: { type: 'normal' | 'right-side' | 'none'; text?: Maybe<string> }
  newsletter?: Maybe<{
    text?: Maybe<string>
    extendText?: Maybe<string>
    title?: Maybe<string>
    subtitle?: string
    emailPlaceholder: string
    button: string
    extendButtonHide: string
    extendButtonExpand: string
    bulletList?: {
      title?: Maybe<string>
      elements?: Maybe<string[]>
    }
  }>
  toast?: { success: string; error: string }
  bulletIcon?: string | null
}

export const Footer = ({
  items,
  logoImg,
  theme = styles,
  partners,
  socials,
  copyright = {
    type: 'normal',
    text: gt.tp('Footer', 'Copyright © {{ year }} RestaurantWeek Sp. z o. o., Aleje Ujazdowskie 24 lok. 23a, 00-478 Warszawa', {
      year: dayjs().year(),
    }),
  },
  newsletter,
  toast = defaultToast(gt),
  bulletIcon = 'check',
}: Props) => {
  const mergedTheme = { ...styles, ...theme }
  const [signUp] = useNewsletterSignUpMutation()
  const toastState = useToast()
  const [extendText, setExtendText] = useState<boolean>(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, touchedFields, isSubmitted },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      marketingConsent: false,
    },
    resolver: yupResolver(joinTheClubSchema),
  })

  const marketingConsentValue = useWatch({ control, name: 'marketingConsent' })

  const handleSignUp = async ({ email, marketingConsent }: { email: string; marketingConsent: boolean }) => {
    if (!marketingConsent) return
    await signUp({
      variables: {
        email,
        agreementText: formatConsentToAPIFormat({
          consentType: UserConsentTypeEnum.MarketingConsent,
          label: <DangerousTranslate text={newsletter?.text} />,
          showMore: <DangerousTranslate text={newsletter?.extendText} />,
          value: true,
        }).agreementText,
      },
      onCompleted: () => {
        toastState?.add({
          type: 'success',
          message: toast.success,
          containerSelector: '#FooterToastsContainer',
          size: 'regular',
        })
      },
      onError: () => {
        toastState?.add({
          type: 'error',
          message: toast.error,
          containerSelector: '#FooterToastsContainer',
          size: 'regular',
        })
      },
    })
  }

  return (
    <footer className={mergedTheme.wrapper}>
      <div className={`container ${mergedTheme.container}`}>
        <div className={mergedTheme.logoContainer}>{logoImg && <img className={mergedTheme.logo} src={logoImg} alt={'Logo'} />}</div>
        <section className={mergedTheme.mainContainer}>
          <div className={mergedTheme.items}>
            {items?.map(column => (
              <div className={mergedTheme.item} key={column.title}>
                <h2 className={mergedTheme.itemTitle}>{column.title}</h2>
                {column.items.map(item => (
                  <Link className={mergedTheme.link} href={item.href} key={item.title}>
                    {item.title}
                  </Link>
                ))}
              </div>
            ))}
          </div>
          <div className={mergedTheme.itemsMobile}>
            {items?.map(column => (
              <Collapse key={column.title} title={column.title} borderStyle='none' size='minimal'>
                <div className={mergedTheme.itemMobile}>
                  {column.items.map(item => (
                    <Link className={mergedTheme.linkMobile} href={item.href} key={item.title}>
                      {item.title}
                    </Link>
                  ))}
                </div>
              </Collapse>
            ))}
          </div>
          {newsletter && (
            <div className={mergedTheme.newsLetterContainer}>
              <div id='FooterToastsContainer' />
              <div className={mergedTheme.newsLetterText}>
                {newsletter.title && <h4 className={`heading4 ${mergedTheme.newsLetterTitle}`}>{newsletter.title}</h4>}
                {newsletter.subtitle && <h5 className={mergedTheme.newsLetterSubtitle}>{newsletter.subtitle}</h5>}
              </div>

              <form onSubmit={handleSubmit(handleSignUp)} className={mergedTheme.newsLetterInputContainer}>
                <div className={mergedTheme.newsLetterInputRow}>
                  <FormInput
                    {...register('email')}
                    placeholder={newsletter.emailPlaceholder}
                    aria-label={newsletter.emailPlaceholder}
                    errorMessage={(touchedFields.email || isSubmitted) && errors.email?.message ? errors.email.message : ''}
                    reserveErrorSpace
                  />

                  <Button type='submit' className={mergedTheme.newsLetterButton} disabled={isSubmitting}>
                    {newsletter.button}
                  </Button>
                </div>

                <div className={mergedTheme.newsLetterConsentRow}>
                  <Checkbox
                    name='marketingConsent'
                    control={control}
                    label=''
                    error={isSubmitted && !marketingConsentValue}
                    disabled={isSubmitting}
                  />
                  <div className={mergedTheme.newsLetterDescription}>
                    <DangerousTranslate text={newsletter.text} />
                    {newsletter.extendText &&
                      (extendText ? (
                        <>
                          <DangerousTranslate text={newsletter.extendText} />
                          <button onClick={() => setExtendText(false)}>{newsletter.extendButtonHide}</button>
                        </>
                      ) : (
                        <>
                          {' '}
                          <button onClick={() => setExtendText(true)}>{newsletter.extendButtonExpand}</button>
                        </>
                      ))}
                  </div>
                </div>

                <p className={mergedTheme.newsLetterConsentError} role='alert'>
                  {isSubmitted && !marketingConsentValue ? gt.tp('Error', 'This field is required') : '\u00A0'}
                </p>
              </form>

              <div className={mergedTheme.newsLetterDescription}>
                {newsletter.bulletList && (
                  <div className={mergedTheme.bulletpointContainer}>
                    <p className={mergedTheme.bulletpointTitle}>{newsletter.bulletList.title}</p>
                    <ul className={mergedTheme.bulletpointList}>
                      {newsletter.bulletList.elements?.map((element, index) => (
                        <li key={index} className={mergedTheme.bulletpointItem}>
                          {bulletIcon && <Icon imgWidth={12} imgHeight={12} className={mergedTheme.bulletpointIcon} icon={bulletIcon} />}
                          {element}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {copyright?.type === 'right-side' && copyright.text && (
                <div className={mergedTheme.newsLetterDescription} dangerouslySetInnerHTML={{ __html: copyright.text }} />
              )}
            </div>
          )}
        </section>
        {partners && (
          <section className={mergedTheme.partnersContainer}>
            {partners?.map(({ partners: partnersImages, title }) => (
              <div className={mergedTheme.partner} key={title}>
                <div className={mergedTheme.partnerTitle}>{title}</div>
                <div className={mergedTheme.partnerLogos}>
                  {partnersImages.map(({ url }) => (
                    <img key={url} alt={title} className={mergedTheme.partnerLogo} src={url} draggable={false} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
        <section className={mergedTheme.socialsContainer}>
          {copyright?.type === 'normal' && copyright.text && (
            <div className={mergedTheme.copyright} dangerouslySetInnerHTML={{ __html: copyright.text }} />
          )}
          {socials && (
            <div className={mergedTheme.socials}>
              {socials.map(social => (
                <Link href={social.link} key={social.link}>
                  <div className={mergedTheme.social}>
                    <img draggable={false} src={social.img} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </footer>
  )
}
